import React, { useEffect, useRef, useState } from "react";
import { logEvent } from "../../../services/eventLog";
import { UserInfoModel } from "../../../models/user/UserInfo";
import { termsOfService, privacyPolicy, refundPolicy } from "../../../constants/Terms";

interface Props {
  show: boolean;
  onHide: () => void;
  onConfirm: (cardInfo: BillingCardInfo) => void;
  userInfo?: UserInfoModel;
  totalPrice: number;
  isLoading: boolean;
}

export interface BillingCardInfo {
  cardNumber: string;
  cardType: string;
  expiryDate: string;
  holderName: string;
  birthOrBusiness: string;
  password2Digits: string;
}

const SubscriptionCardModal: React.FC<Props> = ({
  show,
  onHide,
  onConfirm,
  userInfo,
  totalPrice,
  isLoading,
}) => {
  // 카드 타입 선택 (개인카드/사업자카드)
  const [cardType, setCardType] = useState<"personal" | "business">("personal");

  // 카드 정보 (보안을 위해 로컬/상용 모두 공백으로 시작)
  const [cardNumber1, setCardNumber1] = useState("");
  const [cardNumber2, setCardNumber2] = useState("");
  const [cardNumber3, setCardNumber3] = useState("");
  const [cardNumber4, setCardNumber4] = useState("");
  const [expiryYear, setExpiryYear] = useState("");
  const [expiryMonth, setExpiryMonth] = useState("");

  // 개인카드용
  const [birth, setBirth] = useState("");

  // 사업자카드용
  const [businessNumber, setBusinessNumber] = useState("");

  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // 약관 동의 상태
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [agreeRefund, setAgreeRefund] = useState(false);
  const [agreeAll, setAgreeAll] = useState(false);

  // 계측 2차: 모달을 연 시각·제출 시도 횟수·마지막 검증 실패 칸 (카드 값은 기록하지 않음)
  const openedAtRef = useRef<number>(0);
  const submitAttemptsRef = useRef(0);
  const lastInvalidRef = useRef<string[]>([]);
  useEffect(() => {
    if (show) {
      openedAtRef.current = Date.now();
      submitAttemptsRef.current = 0;
      lastInvalidRef.current = [];
    }
  }, [show]);

  // 어느 칸까지 채웠는지 (값이 아니라 채움 여부만)
  const filledState = () => ({
    number: [cardNumber1, cardNumber2, cardNumber3].every((v) => v.length === 4) && cardNumber4.length >= 3,
    number_partial: !!(cardNumber1 || cardNumber2 || cardNumber3 || cardNumber4),
    expiry: expiryYear.length === 2 && expiryMonth.length === 2,
    owner: cardType === "personal" ? birth.length === 6 : businessNumber.length === 10,
    password: password.length === 2,
    terms: agreeTerms && agreePrivacy && agreeRefund,
  });

  // 약관 모달 상태
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);

  // 카드 번호 포맷팅 (4자리만)
  const formatCardNumber = (value: string) => {
    return (value || "").replace(/\D/g, "").substr(0, 4);
  };

  // 년도 포맷팅 (YY)
  const formatYear = (value: string) => {
    return (value || "").replace(/\D/g, "").substr(0, 2);
  };

  // 월 포맷팅 (MM)
  const formatMonth = (value: string) => {
    return (value || "").replace(/\D/g, "").substr(0, 2);
  };

  // 생년월일 포맷팅 (YYMMDD)
  const formatBirth = (value: string) => {
    return (value || "").replace(/\D/g, "").substr(0, 6);
  };

  // 사업자등록번호 포맷팅 (10자리)
  const formatBusinessNumber = (value: string) => {
    return (value || "").replace(/\D/g, "").substr(0, 10);
  };

  // 비밀번호 포맷팅 (NN)
  const formatPassword = (value: string) => {
    return (value || "").replace(/\D/g, "").substr(0, 2);
  };

  // 전체 동의 핸들러
  const handleAgreeAll = (checked: boolean) => {
    setAgreeAll(checked);
    setAgreeTerms(checked);
    setAgreePrivacy(checked);
    setAgreeRefund(checked);
  };

  // 개별 동의 변경 시 전체 동의 상태 업데이트
  const updateAgreeAll = (terms: boolean, privacy: boolean, refund: boolean) => {
    setAgreeAll(terms && privacy && refund);
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    // 카드 번호 검증 (일반 카드 16자리: 4-4-4-4, AMEX 15자리: 4-4-4-3)
    if (cardNumber1.length !== 4) {
      newErrors.cardNumber1 = "카드 번호 첫 4자리를 입력해주세요.";
    }
    if (cardNumber2.length !== 4) {
      newErrors.cardNumber2 = "카드 번호 두 번째 4자리를 입력해주세요.";
    }
    if (cardNumber3.length !== 4) {
      newErrors.cardNumber3 = "카드 번호 세 번째 4자리를 입력해주세요.";
    }
    // AMEX는 마지막 필드가 3자리, 일반 카드는 4자리
    if (cardNumber4.length < 3 || cardNumber4.length > 4) {
      newErrors.cardNumber4 = "카드 번호 마지막 3-4자리를 입력해주세요.";
    }

    // 유효기간 검증 (MM-YY)
    if (expiryYear.length !== 2) {
      newErrors.expiryYear = "년도 2자리를 입력해주세요.";
    }
    if (expiryMonth.length !== 2) {
      newErrors.expiryMonth = "월 2자리를 입력해주세요.";
    } else {
      const month = parseInt(expiryMonth);
      if (month < 1 || month > 12) {
        newErrors.expiryMonth = "올바른 월(01-12)을 입력해주세요.";
      }
    }

    // 개인정보 검증 (카드 타입에 따라)
    if (cardType === "personal") {
      // 개인카드: 생년월일 검증 (YYMMDD)
      if (birth.length !== 6) {
        newErrors.birth = "생년월일은 6자리(YYMMDD)를 입력해주세요.";
      }
    } else {
      // 사업자카드: 사업자등록번호 검증 (10자리)
      if (businessNumber.length !== 10) {
        newErrors.businessNumber = "사업자등록번호는 10자리를 입력해주세요.";
      }
    }

    // 비밀번호 검증 (앞 2자리)
    if (password.length !== 2) {
      newErrors.password = "카드 비밀번호 앞 2자리를 입력해주세요.";
    }

    // 약관 동의 검증
    if (!agreeTerms) {
      newErrors.agreeTerms = "이용약관에 동의해주세요.";
    }
    if (!agreePrivacy) {
      newErrors.agreePrivacy = "개인정보 처리방침에 동의해주세요.";
    }
    if (!agreeRefund) {
      newErrors.agreeRefund = "환불규정에 동의해주세요.";
    }

    setErrors(newErrors);
    const invalid = Object.keys(newErrors);
    if (invalid.length > 0) {
      lastInvalidRef.current = invalid;
      logEvent("checkout_step", "card_modal_invalid", {
        invalid_fields: invalid,
        card_type: cardType,
        attempt: submitAttemptsRef.current,
      });
    }
    return invalid.length === 0;
  };

  const handleSubmit = () => {
    submitAttemptsRef.current += 1;
    if (!validateForm()) {
      return;
    }

    const cardInfo: BillingCardInfo = {
      cardNumber: `${cardNumber1}-${cardNumber2}-${cardNumber3}-${cardNumber4}`,
      cardType: cardType === "personal" ? "개인카드" : "사업자카드",
      expiryDate: `${expiryMonth}/${expiryYear}`,
      holderName: userInfo?.last_name || "카드 소유자",
      birthOrBusiness: cardType === "personal" ? birth : businessNumber,
      password2Digits: password,
    };

    onConfirm(cardInfo);
  };

  const handleClose = () => {
    // 계측: 제출하지 않고 닫은 경우만 여기로 온다 (제출 성공 시에는 부모가 show 를 끔)
    logEvent("checkout_step", "card_modal_close", {
      filled: filledState(),
      card_type: cardType,
      submit_attempts: submitAttemptsRef.current,
      last_invalid_fields: lastInvalidRef.current,
      seconds_open: openedAtRef.current ? Math.round((Date.now() - openedAtRef.current) / 1000) : null,
    });
    // 모달 닫을 때 초기화
    setCardType("personal");
    setCardNumber1("");
    setCardNumber2("");
    setCardNumber3("");
    setCardNumber4("");
    setExpiryYear("");
    setExpiryMonth("");
    setBirth("");
    setBusinessNumber("");
    setPassword("");
    setErrors({});
    // 약관 동의 초기화
    setAgreeTerms(false);
    setAgreePrivacy(false);
    setAgreeRefund(false);
    setAgreeAll(false);
    onHide();
  };

  return (
    <>
      <style>
        {`
          .subscription-modal {
            max-width: 480px !important;
            width: 90% !important;
            margin: 1.75rem auto;
          }
          
          .subscription-modal .modal-content {
            border-radius: 12px;
            border: none;
            box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
          }
          
          .subscription-modal .form-control {
            height: 38px !important;
            padding: 8px 12px !important;
            font-size: 14px !important;
          }
        `}
      </style>
      <div
        className={`modal fade ${show ? "show" : ""}`}
        style={{
          display: show ? "block" : "none",
          backgroundColor: show ? "rgba(0,0,0,0.5)" : "transparent",
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget && !isLoading) {
            handleClose();
          }
        }}
      >
        <div className="modal-dialog modal-dialog-centered subscription-modal">
          <div className="modal-content" style={{ borderRadius: "12px" }}>
            {/* 헤더 */}
            <div className="d-flex align-items-center justify-content-between p-4 border-bottom">
              <div>
                <h5 className="fw-bold mb-1 d-flex align-items-center">
                  <span className="material-symbols-outlined me-2 text-primary">
                    credit_card
                  </span>
                  구독 결제 카드 등록
                </h5>
                <p className="text-muted mb-0 small">
                  정기결제를 위한 카드 정보를 입력해주세요
                </p>
              </div>
              <button
                onClick={handleClose}
                disabled={isLoading}
                className="btn btn-outline-secondary btn-sm rounded-circle d-flex align-items-center justify-content-center"
                style={{ width: "36px", height: "36px" }}
              >
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: "18px" }}
                >
                  close
                </span>
              </button>
            </div>

            {/* 바디 */}
            <div className="p-4">
              <div className="subscription-card-form">
                {/* 결제 정보 요약 */}
                <div
                  className="payment-summary mb-4 p-3"
                  style={{
                    background: "#f8f9fa",
                    borderRadius: "12px",
                    border: "1px solid #e9ecef",
                  }}
                >
                  <h6 className="mb-2">결제 정보</h6>
                  <div className="d-flex justify-content-between">
                    <span>구매자:</span>
                    <span>{userInfo?.last_name || "구매자"}</span>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>결제 금액:</span>
                    <span className="fw-bold text-primary">
                      {totalPrice.toLocaleString()}원
                    </span>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>카드 타입:</span>
                    <span className="text-info">
                      {cardType === "personal" ? "개인카드" : "사업자카드"}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>결제 방식:</span>
                    <span className="fw-semibold">
                      정기 결제 (자동 갱신)
                    </span>
                  </div>
                </div>

                {/* 카드 타입 선택 */}
                <div className="card-type-selection mb-4">
                  <h6 className="mb-3">카드 타입 선택</h6>
                  <div
                    className="btn-group w-100"
                    role="group"
                    style={{ borderRadius: "10px", overflow: "hidden" }}
                  >
                    <button
                      type="button"
                      className={`btn ${
                        cardType === "personal"
                          ? "btn-primary"
                          : "btn-outline-secondary"
                      }`}
                      onClick={() => setCardType("personal")}
                      style={{
                        flex: 1,
                        fontWeight: cardType === "personal" ? "600" : "400",
                        transition: "all 0.2s ease",
                      }}
                    >
                      <span
                        className="material-symbols-outlined me-2"
                        style={{ fontSize: "18px" }}
                      >
                        person
                      </span>
                      개인카드
                    </button>
                    <button
                      type="button"
                      className={`btn ${
                        cardType === "business"
                          ? "btn-primary"
                          : "btn-outline-secondary"
                      }`}
                      onClick={() => setCardType("business")}
                      style={{
                        flex: 1,
                        fontWeight: cardType === "business" ? "600" : "400",
                        transition: "all 0.2s ease",
                      }}
                    >
                      <span
                        className="material-symbols-outlined me-2"
                        style={{ fontSize: "18px" }}
                      >
                        business
                      </span>
                      사업자카드
                    </button>
                  </div>
                </div>

                {/* 카드 정보 입력 */}
                <div className="card-info-form">
                  {/* 카드 번호 */}
                  <div className="mb-3">
                    <label className="form-label">카드 번호</label>
                    <div className="d-flex gap-2">
                      <input
                        type="text"
                        className={`form-control ${
                          errors.cardNumber1 ? "is-invalid" : ""
                        }`}
                        placeholder="1234"
                        value={cardNumber1}
                        onChange={(e) =>
                          setCardNumber1(formatCardNumber(e.target.value))
                        }
                        maxLength={4}
                        style={{ flex: 1 }}
                      />
                      <span className="align-self-center">-</span>
                      <input
                        type="text"
                        className={`form-control ${
                          errors.cardNumber2 ? "is-invalid" : ""
                        }`}
                        placeholder="5678"
                        value={cardNumber2}
                        onChange={(e) =>
                          setCardNumber2(formatCardNumber(e.target.value))
                        }
                        maxLength={4}
                        style={{ flex: 1 }}
                      />
                      <span className="align-self-center">-</span>
                      <input
                        type="text"
                        className={`form-control ${
                          errors.cardNumber3 ? "is-invalid" : ""
                        }`}
                        placeholder="9012"
                        value={cardNumber3}
                        onChange={(e) =>
                          setCardNumber3(formatCardNumber(e.target.value))
                        }
                        maxLength={4}
                        style={{ flex: 1 }}
                      />
                      <span className="align-self-center">-</span>
                      <input
                        type="text"
                        className={`form-control ${
                          errors.cardNumber4 ? "is-invalid" : ""
                        }`}
                        placeholder="3456"
                        value={cardNumber4}
                        onChange={(e) =>
                          setCardNumber4(formatCardNumber(e.target.value))
                        }
                        maxLength={4}
                        style={{ flex: 1 }}
                      />
                    </div>
                    <div className="form-text text-muted mt-1" style={{ fontSize: '12px' }}>
                      일반 카드: 16자리, AMEX: 15자리
                    </div>
                    {(errors.cardNumber1 ||
                      errors.cardNumber2 ||
                      errors.cardNumber3 ||
                      errors.cardNumber4) && (
                      <div className="invalid-feedback d-block">
                        {errors.cardNumber1 ||
                          errors.cardNumber2 ||
                          errors.cardNumber3 ||
                          errors.cardNumber4}
                      </div>
                    )}
                  </div>

                  {/* 유효기간 */}
                  <div className="mb-3">
                    <label className="form-label">유효기간</label>
                    <div className="d-flex gap-2 align-items-center">
                      <input
                        type="text"
                        className={`form-control ${
                          errors.expiryMonth ? "is-invalid" : ""
                        }`}
                        placeholder="MM"
                        value={expiryMonth}
                        onChange={(e) =>
                          setExpiryMonth(formatMonth(e.target.value))
                        }
                        maxLength={2}
                        style={{ width: "80px" }}
                      />
                      <span>/</span>
                      <input
                        type="text"
                        className={`form-control ${
                          errors.expiryYear ? "is-invalid" : ""
                        }`}
                        placeholder="YY"
                        value={expiryYear}
                        onChange={(e) =>
                          setExpiryYear(formatYear(e.target.value))
                        }
                        maxLength={2}
                        style={{ width: "80px" }}
                      />
                      <span className="text-muted small">(MM/YY)</span>
                    </div>
                    {(errors.expiryMonth || errors.expiryYear) && (
                      <div className="invalid-feedback d-block">
                        {errors.expiryMonth || errors.expiryYear}
                      </div>
                    )}
                  </div>

                  {/* 개인정보 (카드 타입에 따라) */}
                  {cardType === "personal" ? (
                    /* 개인카드: 생년월일 */
                    <div className="mb-3">
                      <label className="form-label">생년월일 (6자리)</label>
                      <input
                        type="text"
                        className={`form-control ${
                          errors.birth ? "is-invalid" : ""
                        }`}
                        placeholder="YYMMDD"
                        value={birth}
                        onChange={(e) => setBirth(formatBirth(e.target.value))}
                        maxLength={6}
                      />
                      {errors.birth && (
                        <div className="invalid-feedback">{errors.birth}</div>
                      )}
                      <div className="form-text">
                        생년월일 6자리를 입력해주세요 (예: 980101)
                      </div>
                    </div>
                  ) : (
                    /* 사업자카드: 사업자등록번호 */
                    <div className="mb-3">
                      <label className="form-label">
                        사업자등록번호 (10자리)
                      </label>
                      <input
                        type="text"
                        className={`form-control ${
                          errors.businessNumber ? "is-invalid" : ""
                        }`}
                        placeholder="0000000000"
                        value={businessNumber}
                        onChange={(e) =>
                          setBusinessNumber(
                            formatBusinessNumber(e.target.value)
                          )
                        }
                        maxLength={10}
                      />
                      {errors.businessNumber && (
                        <div className="invalid-feedback">
                          {errors.businessNumber}
                        </div>
                      )}
                      <div className="form-text">
                        사업자등록번호 10자리를 입력해주세요 (하이픈 제외)
                      </div>
                    </div>
                  )}

                  {/* 카드 비밀번호 */}
                  <div className="mb-3">
                    <label className="form-label">카드 비밀번호 앞 2자리</label>
                    <div className="d-flex gap-2 align-items-center">
                      <input
                        type="password"
                        className={`form-control ${
                          errors.password ? "is-invalid" : ""
                        }`}
                        placeholder="••"
                        value={password}
                        onChange={(e) =>
                          setPassword(formatPassword(e.target.value))
                        }
                        maxLength={2}
                        style={{ width: "80px" }}
                      />
                      <span className="text-dark">**</span>
                      <span className="text-muted small">
                        (뒤 2자리는 숨김)
                      </span>
                    </div>
                    {errors.password && (
                      <div className="invalid-feedback">{errors.password}</div>
                    )}
                  </div>
                </div>

                {/* 약관 동의 섹션 */}
                <div
                  className="agreements-section mt-4 p-3"
                  style={{
                    background: "#f8f9fa",
                    borderRadius: "12px",
                    border: "1px solid #e9ecef",
                  }}
                >
                  <h6 className="mb-3 d-flex align-items-center">
                    <span
                      className="material-symbols-outlined me-2"
                      style={{ fontSize: "18px" }}
                    >
                      gavel
                    </span>
                    약관 동의
                  </h6>

                  {/* 전체 동의 */}
                  <div className="form-check mb-3 pb-2 border-bottom">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="agreeAll"
                      checked={agreeAll}
                      onChange={(e) => handleAgreeAll(e.target.checked)}
                    />
                    <label
                      className="form-check-label fw-bold"
                      htmlFor="agreeAll"
                    >
                      전체 동의
                    </label>
                  </div>

                  {/* 이용약관 */}
                  <div className="form-check mb-2 d-flex align-items-center">
                    <input
                      className={`form-check-input ${
                        errors.agreeTerms ? "is-invalid" : ""
                      }`}
                      type="checkbox"
                      id="agreeTerms"
                      checked={agreeTerms}
                      onChange={(e) => {
                        setAgreeTerms(e.target.checked);
                        updateAgreeAll(
                          e.target.checked,
                          agreePrivacy,
                          agreeRefund
                        );
                      }}
                    />
                    <label
                      className="form-check-label flex-grow-1"
                      htmlFor="agreeTerms"
                    >
                      [필수] 이용약관 동의
                    </label>
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 text-muted"
                      onClick={() => setShowTermsModal(true)}
                    >
                      보기
                    </button>
                  </div>

                  {/* 개인정보 처리방침 */}
                  <div className="form-check mb-2 d-flex align-items-center">
                    <input
                      className={`form-check-input ${
                        errors.agreePrivacy ? "is-invalid" : ""
                      }`}
                      type="checkbox"
                      id="agreePrivacy"
                      checked={agreePrivacy}
                      onChange={(e) => {
                        setAgreePrivacy(e.target.checked);
                        updateAgreeAll(
                          agreeTerms,
                          e.target.checked,
                          agreeRefund
                        );
                      }}
                    />
                    <label
                      className="form-check-label flex-grow-1"
                      htmlFor="agreePrivacy"
                    >
                      [필수] 개인정보 처리방침 동의
                    </label>
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 text-muted"
                      onClick={() => setShowPrivacyModal(true)}
                    >
                      보기
                    </button>
                  </div>

                  {/* 환불규정 */}
                  <div className="form-check mb-2 d-flex align-items-center">
                    <input
                      className={`form-check-input ${
                        errors.agreeRefund ? "is-invalid" : ""
                      }`}
                      type="checkbox"
                      id="agreeRefund"
                      checked={agreeRefund}
                      onChange={(e) => {
                        setAgreeRefund(e.target.checked);
                        updateAgreeAll(
                          agreeTerms,
                          agreePrivacy,
                          e.target.checked
                        );
                      }}
                    />
                    <label
                      className="form-check-label flex-grow-1"
                      htmlFor="agreeRefund"
                    >
                      [필수] 환불규정 동의
                    </label>
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 text-muted"
                      onClick={() => setShowRefundModal(true)}
                    >
                      보기
                    </button>
                  </div>

                  {/* 동의 에러 메시지 */}
                  {(errors.agreeTerms ||
                    errors.agreePrivacy ||
                    errors.agreeRefund) && (
                    <div className="text-danger small mt-2">
                      {errors.agreeTerms ||
                        errors.agreePrivacy ||
                        errors.agreeRefund}
                    </div>
                  )}
                </div>

                {/* 자동 갱신 안내 */}
                <div
                  className="auto-renewal-notice mt-3 p-3"
                  style={{
                    background: "#fff3cd",
                    borderRadius: "8px",
                    border: "1px solid #ffc107",
                  }}
                >
                  <div className="d-flex align-items-start">
                    <span
                      className="material-symbols-outlined me-2 text-warning"
                      style={{ fontSize: "20px" }}
                    >
                      info
                    </span>
                    <div className="small">
                      <strong>자동 갱신 안내</strong>
                      <p className="mb-0 mt-1">
                        본 구독은 결제 주기에 따라 자동으로 갱신되며, 해지하지
                        않으면 동일 금액이 자동 결제됩니다. 해지는 마이페이지
                        또는 고객센터(ddk@ddukeong.com)를 통해 가능합니다.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 푸터 */}
            <div className="d-flex justify-content-end gap-3 p-4 border-top">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={handleClose}
                disabled={isLoading}
                style={{ borderRadius: "8px", minWidth: "100px" }}
              >
                취소
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSubmit}
                disabled={isLoading}
                style={{ borderRadius: "8px", minWidth: "140px" }}
              >
                {isLoading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    처리중...
                  </>
                ) : (
                  <>
                    <span className="material-symbols-outlined me-2">
                      payment
                    </span>
                    구독 결제하기
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 이용약관 모달 */}
      {showTermsModal && (
        <div
          className="modal fade show"
          style={{
            display: "block",
            backgroundColor: "rgba(0,0,0,0.7)",
            zIndex: 1060,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowTermsModal(false);
          }}
        >
          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg">
            <div className="modal-content" style={{ borderRadius: "12px" }}>
              <div className="modal-header">
                <h5 className="modal-title fw-bold">이용약관</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowTermsModal(false)}
                ></button>
              </div>
              <div className="modal-body" style={{ maxHeight: "60vh" }}>
                <pre
                  style={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "keep-all",
                    fontFamily: "inherit",
                    fontSize: "14px",
                    lineHeight: "1.6",
                  }}
                >
                  {termsOfService}
                </pre>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setShowTermsModal(false)}
                >
                  확인
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 개인정보 처리방침 모달 */}
      {showPrivacyModal && (
        <div
          className="modal fade show"
          style={{
            display: "block",
            backgroundColor: "rgba(0,0,0,0.7)",
            zIndex: 1060,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowPrivacyModal(false);
          }}
        >
          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg">
            <div className="modal-content" style={{ borderRadius: "12px" }}>
              <div className="modal-header">
                <h5 className="modal-title fw-bold">개인정보 처리방침</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowPrivacyModal(false)}
                ></button>
              </div>
              <div className="modal-body" style={{ maxHeight: "60vh" }}>
                <pre
                  style={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "keep-all",
                    fontFamily: "inherit",
                    fontSize: "14px",
                    lineHeight: "1.6",
                  }}
                >
                  {privacyPolicy}
                </pre>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setShowPrivacyModal(false)}
                >
                  확인
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 환불규정 모달 */}
      {showRefundModal && (
        <div
          className="modal fade show"
          style={{
            display: "block",
            backgroundColor: "rgba(0,0,0,0.7)",
            zIndex: 1060,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowRefundModal(false);
          }}
        >
          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg">
            <div className="modal-content" style={{ borderRadius: "12px" }}>
              <div className="modal-header">
                <h5 className="modal-title fw-bold">환불 및 청약철회 규정</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowRefundModal(false)}
                ></button>
              </div>
              <div className="modal-body" style={{ maxHeight: "60vh" }}>
                <pre
                  style={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "keep-all",
                    fontFamily: "inherit",
                    fontSize: "14px",
                    lineHeight: "1.6",
                  }}
                >
                  {refundPolicy}
                </pre>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setShowRefundModal(false)}
                >
                  확인
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SubscriptionCardModal;
